import { cjk } from '@streamdown/cjk';
import { createCodePlugin, type CodeHighlighterPlugin, type HighlightResult } from '@streamdown/code';
import { createMathPlugin } from '@streamdown/math';
import { mermaid } from '@streamdown/mermaid';
import remarkGfm from 'remark-gfm';
import type { BundledLanguage, BundledTheme, PluginConfig } from 'streamdown';

/**
 * Shared Streamdown configuration for Nova components.
 *
 * All markdown rendering components should use these shared plugins
 * to ensure consistent rendering across the application.
 *
 * Note: Apps using Nova components must import the required CSS:
 * - `import 'katex/dist/katex.min.css'` for math rendering
 * - `import 'streamdown/styles.css'` for streaming animations
 */

// Configure math plugin — only $$ delimiters for display math
// Single $ is too common in insurance/financial text (e.g. $84,996.65)
export const math = createMathPlugin({ singleDollarTextMath: false });

// Shiki theme for syntax highlighting [light, dark]
export const shikiTheme: [BundledTheme, BundledTheme] = ['github-light', 'github-dark'];

const CODE_FENCE_LANGUAGE_ALIASES = {
  excel: 'csv',
  spreadsheet: 'csv',
  xls: 'csv',
  xlsx: 'csv',
} as const satisfies Record<string, BundledLanguage>;

function createPlainTextHighlightResult(code: string): HighlightResult {
  const lines = code.split(/\r?\n/);

  return {
    bg: 'transparent',
    fg: 'inherit',
    tokens: lines.map((line) => [
      {
        content: line,
        color: 'inherit',
        bgColor: 'transparent',
        htmlStyle: {},
        offset: 0,
      },
    ]),
  };
}

export function resolveCodeFenceLanguage(
  language: string,
  supportsLanguage: (language: BundledLanguage) => boolean,
): BundledLanguage | null {
  const normalizedLanguage = language.trim().toLowerCase();

  if (!normalizedLanguage) {
    return null;
  }

  const aliasedLanguage = CODE_FENCE_LANGUAGE_ALIASES[normalizedLanguage as keyof typeof CODE_FENCE_LANGUAGE_ALIASES];
  if (aliasedLanguage) {
    return aliasedLanguage;
  }

  return supportsLanguage(normalizedLanguage as BundledLanguage) ? (normalizedLanguage as BundledLanguage) : null;
}

export function createSafeCodePlugin(basePlugin: CodeHighlighterPlugin): CodeHighlighterPlugin {
  return {
    ...basePlugin,
    supportsLanguage(language) {
      return resolveCodeFenceLanguage(language, basePlugin.supportsLanguage) !== null;
    },
    highlight(options, callback) {
      const resolvedLanguage = resolveCodeFenceLanguage(options.language, basePlugin.supportsLanguage);

      if (!resolvedLanguage) {
        return createPlainTextHighlightResult(options.code);
      }

      return basePlugin.highlight(
        {
          ...options,
          language: resolvedLanguage,
        },
        callback,
      );
    },
  };
}

const safeCodePlugin = createSafeCodePlugin(createCodePlugin({ themes: shikiTheme }));

// All streamdown plugins for full feature support
// Type assertion needed due to unified version mismatch between plugins
export const streamdownPlugins = { cjk, code: safeCodePlugin, math, mermaid } as PluginConfig;

// Remark plugins for GFM (tables, strikethrough, autolinks, etc.)
export const remarkPlugins = [remarkGfm];
